import { useState } from 'react';
import { Link } from 'react-router-dom';

const industries = [
  {
    id: 'hair',
    label: 'Hair & Beauty',
    icon: 'ri-scissors-2-line',
    headline: 'Fill your chair, reduce no-shows',
    subtext:
      'Clients message at all hours. Your AI responds instantly, books the slot, sends reminders and follows up if they go quiet.',
    flow: [
      { icon: 'ri-message-2-line', label: 'Client messages asking about availability' },
      { icon: 'ri-robot-2-line', label: 'AI replies instantly with available slots' },
      { icon: 'ri-calendar-check-line', label: 'Appointment booked automatically' },
      { icon: 'ri-notification-3-line', label: 'Reminder sent 24 hrs before' },
      { icon: 'ri-refresh-line', label: 'Follow-up sent to rebook after visit' },
    ],
    stats: [
      { value: '40%', label: 'Fewer no-shows' },
      { value: '3x', label: 'Faster booking response' },
      { value: '24/7', label: 'Available to clients' },
    ],
    image: 'https://public.readdy.ai/ai/img_res/526dd9c6491c981febdfdc73a7f76386.jpg',
  },
  {
    id: 'dental',
    label: 'Dental & Clinics',
    icon: 'ri-heart-pulse-line',
    headline: 'Keep your appointment book full',
    subtext:
      'Patients rarely call during opening hours. Your AI handles enquiries at any time, books appointments, and sends confirmation and reminder sequences automatically.',
    flow: [
      { icon: 'ri-message-2-line', label: 'Patient enquires about an appointment' },
      { icon: 'ri-robot-2-line', label: 'AI qualifies their needs and offers slots' },
      { icon: 'ri-calendar-check-line', label: 'Appointment booked and confirmed by email' },
      { icon: 'ri-notification-3-line', label: 'Automated reminder 48 hrs before visit' },
      { icon: 'ri-mail-send-line', label: 'Post-visit follow-up and rebooking prompt' },
    ],
    stats: [
      { value: '60%', label: 'Less phone time' },
      { value: '2x', label: 'More bookings captured' },
      { value: '24/7', label: 'Patient response' },
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20clean%20dental%20clinic%20reception%20interior%20with%20white%20walls%20professional%20medical%20equipment%20calm%20welcoming%20atmosphere%20natural%20light%20minimal%20aesthetic%20high%20quality%20photography&width=600&height=420&seq=industry-dental-1&orientation=landscape',
  },
  {
    id: 'trades',
    label: 'Builders & Trades',
    icon: 'ri-hammer-line',
    headline: 'Turn quote requests into confirmed jobs',
    subtext:
      'Homeowners search late at night. Your AI captures their enquiry, gathers job details, books a site visit and follows up automatically while you focus on the work.',
    flow: [
      { icon: 'ri-message-2-line', label: 'Homeowner enquires about a job or quote' },
      { icon: 'ri-robot-2-line', label: 'AI collects job details and location' },
      { icon: 'ri-calendar-check-line', label: 'Site visit booked at a convenient time' },
      { icon: 'ri-notification-3-line', label: 'Confirmation and reminder sent automatically' },
      { icon: 'ri-refresh-line', label: 'Follow-up sequence if no response after quote' },
    ],
    stats: [
      { value: '5x', label: 'More enquiries captured' },
      { value: '80%', label: 'Less admin time' },
      { value: '24/7', label: 'Enquiry handling' },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20builder%20tradesperson%20at%20a%20modern%20home%20renovation%20project%20clean%20tidy%20workspace%20tools%20neatly%20arranged%20sunlit%20interior%20construction%20professional%20quality%20photography%20minimal%20background&width=600&height=420&seq=industry-trades-1&orientation=landscape',
  },
  {
    id: 'fitness',
    label: 'Fitness & Wellness',
    icon: 'ri-run-line',
    headline: 'Keep your schedule packed and clients retained',
    subtext:
      'Members drop off when life gets busy. Your AI re-engages them automatically, fills empty slots and sends personalised follow-ups to keep them coming back.',
    flow: [
      { icon: 'ri-message-2-line', label: 'Prospect asks about classes or sessions' },
      { icon: 'ri-robot-2-line', label: 'AI explains options and books a trial slot' },
      { icon: 'ri-calendar-check-line', label: 'Session booked and confirmed instantly' },
      { icon: 'ri-notification-3-line', label: 'Reminder and motivational message sent' },
      { icon: 'ri-refresh-line', label: 'Re-engagement sequence for lapsed clients' },
    ],
    stats: [
      { value: '35%', label: 'Better client retention' },
      { value: '2x', label: 'Trial bookings' },
      { value: '24/7', label: 'Always responsive' },
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20bright%20gym%20fitness%20studio%20interior%20with%20clean%20equipment%20minimal%20design%20natural%20light%20wooden%20floors%20professional%20wellness%20centre%20high%20quality%20photography%20inviting%20atmosphere&width=600&height=420&seq=industry-fitness-1&orientation=landscape',
  },
  {
    id: 'home-services',
    label: 'Home Services',
    icon: 'ri-home-heart-line',
    headline: 'Book more jobs, chase zero leads manually',
    subtext:
      'Cleaners, gardeners, window cleaners, and property maintenance businesses grow through repeat bookings and referrals. AI handles your bookings and keeps clients coming back.',
    flow: [
      { icon: 'ri-message-2-line', label: 'Client enquires about a regular service' },
      { icon: 'ri-robot-2-line', label: 'AI responds with pricing and availability' },
      { icon: 'ri-calendar-check-line', label: 'Job booked and recurring schedule set' },
      { icon: 'ri-notification-3-line', label: 'Day-before confirmation sent automatically' },
      { icon: 'ri-star-line', label: 'Review request sent after every visit' },
    ],
    stats: [
      { value: '92%', label: 'Client retention with follow-up' },
      { value: '3x', label: 'More referrals generated' },
      { value: '6hrs', label: 'Saved weekly on admin' },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20home%20cleaning%20service%20modern%20bright%20clean%20house%20interior%20minimalist%20living%20room%20premium%20quality%20domestic%20cleaning%20service%20light%20natural%20tones%20high%20quality%20photography&width=600&height=420&seq=industry-home-services-1&orientation=landscape',
  },
  {
    id: 'physio-therapy',
    label: 'Physio & Therapy',
    icon: 'ri-stethoscope-line',
    headline: 'Remove the admin, focus on your patients',
    subtext:
      'Physiotherapists, osteopaths, and therapists spend too much time on admin. AI handles new patient intake, appointment scheduling, and follow-up so you can focus on what you do best.',
    flow: [
      { icon: 'ri-message-2-line', label: 'Patient enquires about treatment or assessment' },
      { icon: 'ri-robot-2-line', label: 'AI captures details and explains the process' },
      { icon: 'ri-calendar-check-line', label: 'Initial assessment booked instantly' },
      { icon: 'ri-file-text-line', label: 'Pre-session intake forms sent automatically' },
      { icon: 'ri-refresh-line', label: 'Post-session follow-up and rebooking handled' },
    ],
    stats: [
      { value: '68%', label: 'Rebook with timely follow-up' },
      { value: '2hrs', label: 'Daily admin eliminated' },
      { value: '45%', label: 'More new patient conversions' },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20physiotherapy%20clinic%20treatment%20room%20calm%20serene%20minimal%20modern%20equipment%20clean%20neutral%20tones%20premium%20healthcare%20practice%20interior%20high%20quality%20photography&width=600&height=420&seq=industry-physio-1&orientation=landscape',
  },
];

const IndustriesSection = () => {
  const [activeId, setActiveId] = useState('hair');
  const active = industries.find((i) => i.id === activeId)!;

  return (
    <section className="py-24 bg-ea-black relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, #0ea5e9 1px, transparent 1px)', backgroundSize: '48px 48px' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
            <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Built For Your Industry</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Automation That Fits{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
              Your Business
            </span>
          </h2>
          <p className="text-ea-gray text-lg max-w-2xl mx-auto">
            Whether you run a salon, a clinic, a trades business or a gym, the process is the same: someone enquires, the AI handles it, you get a confirmed booking.
          </p>
        </div>

        {/* Tab buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {industries.map((ind) => (
            <button
              key={ind.id}
              onClick={() => setActiveId(ind.id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeId === ind.id
                  ? 'bg-ea-blue border-ea-blue text-white'
                  : 'bg-ea-dark-card border-ea-dark-border text-ea-gray hover:border-ea-blue/40 hover:text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${ind.icon} text-sm`}></i>
              </div>
              {ind.label}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <div className="bg-ea-dark-card border border-ea-dark-border rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left: text content */}
            <div className="p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-ea-blue/10 border border-ea-blue/20 mb-6">
                  <i className={`${active.icon} text-ea-blue text-xl`}></i>
                </div>

                <h3 className="text-white font-bold text-2xl lg:text-3xl mb-3">{active.headline}</h3>
                <p className="text-ea-gray text-sm leading-relaxed mb-8">{active.subtext}</p>

                {/* Flow steps */}
                <div className="space-y-3 mb-8">
                  {active.flow.map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20">
                          <i className={`${step.icon} text-ea-blue text-xs`}></i>
                        </div>
                        {i < active.flow.length - 1 && (
                          <div className="w-4 h-px bg-ea-blue/20"></div>
                        )}
                      </div>
                      <span className="text-ea-gray text-xs leading-snug">{step.label}</span>
                    </div>
                  ))}
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-6 pt-4 border-t border-ea-dark-border">
                  {active.stats.map((stat) => (
                    <div key={stat.label}>
                      <p className="text-white font-bold text-xl">{stat.value}</p>
                      <p className="text-ea-gray text-xs">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                >
                  Book a Free Demo <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Right: image */}
            <div className="relative h-64 lg:h-auto min-h-72">
              <img
                src={active.image}
                alt={active.label}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-ea-dark-card via-transparent to-transparent lg:block hidden"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-ea-dark-card via-transparent to-transparent lg:hidden"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
